import React, { ComponentProps } from 'react';

import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import theme from '@themes/theme';

type BaseProps = {
  iconName: ComponentProps<typeof Icon>['name'];
  title: string;
  description: string;
  ctaLabel: string;
  onCta: () => void;
  bottom?: number;
  // Render in normal layout flow (margins) instead of floating absolutely
  // over the content above. Used in chat so the nudge never obscures the
  // message list. `bottom` is ignored when inline.
  inline?: boolean;
};

// "Later" is both-or-neither: omit both to render a single full-width CTA
// (e.g. the "Get a site photo" nudge). The union enforces this at compile time
// so a call site can't pass a label without its handler (or vice versa).
type LaterProps =
  | { laterLabel: string; onLater: () => void }
  | { laterLabel?: undefined; onLater?: undefined };

type Props = BaseProps & LaterProps;

const NudgeBanner: React.FC<Props> = ({
  iconName,
  title,
  description,
  laterLabel,
  ctaLabel,
  onLater,
  onCta,
  bottom = theme.spacing.m,
  inline = false,
}) => (
  <Box
    borderRadius='xs'
    padding='s'
    marginHorizontal={inline ? 'm' : undefined}
    marginBottom={inline ? 'xs' : undefined}
    style={{
      backgroundColor: theme.colors.black,
      shadowColor: theme.colors.black,
      shadowOffset: { width: 0, height: 5 },
      shadowOpacity: 0.05,
      shadowRadius: 25,
      elevation: 8,
      ...(inline
        ? {}
        : {
            position: 'absolute',
            bottom,
            left: theme.spacing.m,
            right: theme.spacing.m,
            zIndex: 10,
          }),
    }}>
    <Box
      flexDirection='row'
      alignItems='center'
      marginBottom='xs'
      style={{ gap: theme.spacing.xs }}>
      <Box
        borderRadius='m'
        width={32}
        height={32}
        alignItems='center'
        justifyContent='center'
        backgroundColor='transparentGreenDark'>
        <Icon name={iconName} variant='m' color='greenBrand' />
      </Box>
      <Text variant='labelEmphasizedNudgeTitle' style={{ flex: 1 }}>
        {title}
      </Text>
    </Box>
    <Text variant='metadata' color='white' marginBottom='m'>
      {description}
    </Text>
    <Box
      flexDirection='row'
      alignItems='center'
      style={{ gap: theme.spacing.s }}>
      {laterLabel && onLater && (
        <Button
          isSmall
          variant='text'
          onPress={onLater}
          width={100}
          height={48}
          paddingVertical='none'
          btnTextColor='white'>
          {laterLabel}
        </Button>
      )}
      <Box flex={1}>
        <Button
          isSmall
          variant='primary'
          onPress={onCta}
          height={48}
          backgroundColor='greenSecondary'
          borderRadius='s'
          paddingVertical='none'>
          {ctaLabel}
        </Button>
      </Box>
    </Box>
  </Box>
);

export default NudgeBanner;
